<template>
  <div class="row">
    <div class="col-sm-2">
      <OverviewBox 
        title="Account Billing"
        :loading="loading"
        avatarImage="/glass/img/admin-overview-billing.png"
        :avatarCircleMode="false" >

        <h4>{{accountData.companyName}}</h4>

        <OverviewBoxList>
          <OverviewBoxListItem title="Base Subscription">
            <span v-if="activeCoreSubscription" class="status-active">Active</span>
            <span v-if="!activeCoreSubscription" class="status-archive">Inactive</span>
          </OverviewBoxListItem>
          <template v-if="activeCoreSubscription">
            <OverviewBoxListItem title="Auto Renew">{{activeCoreSubscription.isAutoRenewEnabled | formatBoolean}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Expiry">{{activeCoreSubscription.expiryDate | formatDate}}</OverviewBoxListItem>
          </template>
        </OverviewBoxList>
      </OverviewBox>
    </div>
    <div class="col-sm-10">
      <TabbedBox :loading="loading">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-billing-main">
            <i class="far fa-credit-card"></i> Billing Overview
          </Tab>
          <Tab tabid="tab-billing-details">
            <i class="far fa-list"></i> Details
          </Tab>
          <Tab tabid="tab-billing-address">
            <i class="far fa-address-card"></i> Billing Address
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-billing-main">
            <DataPageArea title="Base Subscription">
              <template slot="actionArea">
                <!-- Note: Disabled now until Stripe access fully supported -->
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.billing) && false" class="float-right">
                  <DropdownMenuItem @click="purchaseNewSubscription(false)" icon="fa-shopping-cart">Purchase New Subscription</DropdownMenuItem>
                  <DropdownMenuItem v-if="activeCoreSubscription" @click="purchaseNewSubscription(true)" icon="fa-shopping-cart">Purchase Addons</DropdownMenuItem>
                  <DropdownMenuItem v-if="billingSubscriptionItems.length > 0" @click="manageSubscription" icon="fa-pencil">Manage Subscription</DropdownMenuItem>
                </ActionDropMenu>
              </template>
              <template slot="dataArea">
                <DataItem title="Subscription Status">
                  <span v-if="activeCoreSubscription" class="status-active">Active</span>
                  <span v-if="!activeCoreSubscription" class="status-archive">Inactive</span>
                </DataItem>
                <DataItem title="Subscription Info">
                  <div v-if="activeCoreSubItem">
                    {{activeCoreSubItem.name}}<br />
                    {{activeCoreSubItem.description}}
                  </div>
                </DataItem>
                <DataItem title="Subscription Expiry">
                  <div v-if="activeCoreSubscription">
                    {{activeCoreSubscription.expiryDate | formatDate}}
                  </div>
                </DataItem>
                <DataItem title="Subscription Auto Renew">
                  <div v-if="activeCoreSubscription">
                    {{activeCoreSubscription.isAutoRenewEnabled | formatBoolean}}
                  </div>
                </DataItem>                
              </template>
            </DataPageArea>

            <FormHorizontalRule />

            <DataPageArea title="Plan Details">
              <template slot="dataArea">
                <DataItem title="Active Users Limit">
                  {{currentActiveUserCount}} / {{productAccessUserLimit}} User{{displayCountPlural(productAccessUserLimit)}}
                </DataItem>
                <DataItem title="Document Storage Limit">
                  {{formatDecimal(currentStorageUsage, 2, true)}} / {{formatDecimal(productAccessStorageLimit, 2, true)}} GB
                </DataItem>
                <DataItem title="Remaining Support Time">
                  {{formatDuration(remainingSupportTime * 60, { hideSeconds: true })}} Hours
                </DataItem>
                <DataItem title="Remaining SMS Credits">
                  {{remainingSMSCredits}} Credits
                </DataItem>
              </template>
            </DataPageArea>
          </TabPage>
          <TabPage tabid="tab-billing-details">
            <ContentTitle>Subscriptions</ContentTitle>
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Quantity</th>
                  <th>Status</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="subItem in billingSubscriptionItems" :key="subItem.id">
                  <td class="font-weight-bold">
                    {{subItem.name}}
                  </td>
                  <td>
                    {{subItem.description}}
                  </td>
                  <td>
                    {{subItem.quantity}}
                  </td>
                  <td>
                    <div v-if="subItem.isAutoRenewEnabled">
                      <LabelBadge labelClass="label-status-active">ACTIVE</LabelBadge><br />
                      <strong>Next renewal</strong> - {{formatDate(subItem.expiryDate)}}
                    </div>
                    <div v-if="!subItem.isAutoRenewEnabled">
                      <LabelBadge labelClass="label-status-pending">STOPPED</LabelBadge><br />
                      <strong>Expires</strong> - {{formatDate(subItem.expiryDate)}}
                    </div>
                  </td>
                </tr>
              </template>
              <template v-slot:empty v-if="billingSubscriptionItems.length == 0">No subscriptions found</template>
            </StaticDataTable>

            <FormHorizontalRule />

            <div class="mt-4">
              <ContentTitle>Purchased Products</ContentTitle>
              <StaticDataTable class="static-table-list">
                <template v-slot:header>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>Purchased</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <tr v-for="item in billingPurchasedProducts" :key="item.id">
                    <td class="font-weight-bold">
                      {{item.name}}
                    </td>
                    <td>
                      {{item.description}}
                    </td>
                    <td>
                      {{item.quantity}}
                    </td>
                    <td>
                      {{formatDateTime(item.createdAt)}}
                    </td>
                  </tr>
                </template>
                <template v-slot:empty v-if="billingPurchasedProducts.length == 0">No products found</template>
              </StaticDataTable>
            </div>
          </TabPage>

          <TabPage tabid="tab-billing-address">
            <DataPageArea title="Billing Details">
              <template slot="actionArea">
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.billing)" class="float-right">
                  <DropdownMenuItem @click="editBillingAddress" icon="fa-pencil">Edit Billing Address</DropdownMenuItem>
                </ActionDropMenu>
              </template>
              <template slot="dataArea">
                <DataItem title="Billing Address" v-if="billingData && billingData.account">
                  <AddressDisplay
                    :address1="billingData.account.billingAddress1"
                    :address2="billingData.account.billingAddress2"
                    :suburb="billingData.account.billingSuburb"
                    :postcode="billingData.account.billingPostcode"
                    :state="billingData.account.billingState"
                    :country="billingData.account.billingCountry"
                    />                  
                </DataItem>
              </template>
            </DataPageArea>

          </TabPage>

        </template>
      </TabbedBox>
    </div>
  </div>
</template>

<script>
import StaticDataTable from '@/components/table/staticDataTable.vue'
import BillingProductListDialog from '@/modules/admin/views/billingProductListDialog'
import AccountBillingEditDialog from '@/modules/admin/views/accountBillingEditDialog'
import ProcessingDialog from '@/components/dialog/processingDialog.vue'

export default {
  name: "AccountBillingAdmin",
  components: {
    StaticDataTable
  },
  data: () => {
    return {
      loading: true,
      accountData: {
        subscription: { isActive: false }
      },
      billingData: {}
    };
  },
  computed: {
    productAccessUserLimit() {
      return this.billingData.userLimit
    },
    productAccessStorageLimit() {
      return this.billingData.fileStorageLimit
    },
    productFeatureList() {
      if(this.accountData.accountProductAccess)
        return this.accountData.accountProductAccess.featureList
      return []
    },
    currentStorageUsage() {
      return this.billingData.totalFileStorageSize
    },
    currentActiveUserCount() {
      return this.billingData.userActiveCount
    },
    remainingSupportTime() {
      var totalUsedMinutesTime = this.billingData.totalSupportUsedMinutes
      return Math.max((this.billingData.supportPurchasedMinutes - totalUsedMinutesTime), 0)
    },
    remainingSMSCredits() {
      var totalUsedSMSCredits = this.billingData.totalSMSUsedCredits
      return Math.max((this.billingData.availableSMSCredits - totalUsedSMSCredits), 0)
    },
    billingSubscriptionItems() {
      let subItems = []

      if(this.billingData.subscriptionList) {
        this.billingData.subscriptionList.filter(sub => sub.isActive).forEach(sub => {
          if(sub.subscriptionItems) {
            sub.subscriptionItems.forEach(subItem => {
              subItems.push({
                id: subItem.id,
                isAutoRenewEnabled: sub.isAutoRenewEnabled,
                expiryDate: sub.expiryDate,
                quantity: subItem.quantity,

                isAddonProduct: subItem.isAddonProduct,
                name: subItem.name,
                description: subItem.description,
                availableUserCount: subItem.availableUserCount,
                availableStorageGigabytes: subItem.availableStorageGigabytes,
                availableSupportMinutes: subItem.availableSupportMinutes,
                featureList: subItem.featureList
              })
            })
          }
        })
      }
      return subItems
    },
    billingPurchasedProducts() {
      let purchasedProducts = []

      if(this.billingData.purchasedProductList) {
        this.billingData.purchasedProductList.filter(productInfo => productInfo.isActive).forEach(productInfo => {
          purchasedProducts.push({
            id: productInfo.id,
            quantity: productInfo.quantity,
            createdAt: productInfo.createdAt,

            isAddonProduct: productInfo.isAddonProduct,
            name: productInfo.name,
            description: productInfo.description,
            availableUserCount: productInfo.availableUserCount,
            availableStorageGigabytes: productInfo.availableStorageGigabytes,
            availableSupportMinutes: productInfo.availableSupportMinutes,
            featureList: productInfo.featureList
          })
        })
      }
      return purchasedProducts
    },
    activeCoreSubscription() {
      if(!this.billingData.subscriptionList)
        return null

      return this.billingData.subscriptionList.filter(sub => sub.isActive).find(sub => {
        // Has to have at least 1 non-addon sub item
        if(!sub.subscriptionItems)
          return false
        return (sub.subscriptionItems.filter(subItem => !subItem.isAddonProduct).length > 0)
      })
    },
    activeCoreSubItem() {
      return this.billingSubscriptionItems.find(subItem => !subItem.isAddonProduct)
    }
    
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true

      // Get the account details
      Promise.all([
        this.$apiService.adminService.getAccount(),
        this.$apiService.adminService.getAccountBilling()
      ]).then(results => {
        this.$data.accountData = results[0].data
        this.$data.billingData = results[1].data
      })
      .finally(() => {
        this.loading = false
      })
    },
    purchaseNewSubscription(displayAddonProducts) {
      this.displayModal(BillingProductListDialog, {
        displayAddonProducts: displayAddonProducts
      })
      .then(() => {
      })
    },
    manageSubscription() {
      this.displayModal(ProcessingDialog, {
        title: 'Billing management',
        message: 'Redirecting to the billing management page',
        onBeforeOpen: (instance) => {
          this.$apiService.adminService.createBillingPortalSession()
          .then(response => {
              setTimeout(() => {
                window.location.href = response.data.url
              }, 1000)
          })
          .catch(err => {
            instance.hide()
            this.displayNetworkError(err, 'Failed to connect', 'Error while attempting to connect to billing system')
          })
        }
      })
    },
    editBillingAddress() {
      this.displayModal(AccountBillingEditDialog, { 
      }, this.billingData.account)
      .then((success) => {
        if(success) {
          this.successToast('Billing Updated')
          this.requestData()
        }
      })
    }
  }
}

</script>